const { debounce, viewportSize } = window.BAO.Utils

class ProductMedia extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['items', 'item', 'button']
  }

  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.inViewImage = null
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      document,
      'drawerOpen',
      debounce(this.onDrawerOpen.bind(this), 200),
    )

    this.config.elements.mediaImages.forEach(mediaImageEl => {
      this.pinchZoom(mediaImageEl)
    })

    if (this.els?.button?.exists) {
      this.els.button.elements.forEach(button => {
        this.listeners.add(button, 'click', this.toggleDesktopZoom.bind(this))
      })
    }

    if (this.els?.thumbnail?.exists) {
      this.els.thumbnail.elements.forEach(thumbnail => {
        this.listeners.add(thumbnail, 'click', this.onThumbnailClick.bind(this))
      })
    }

    // Scroll management for desktop and mobile
    this.addEventListener('wheel', this.handleWheelEvent, { passive: false })

    this.handleTouchStartScrollToMedia = this.handleTouchStartScrollToMedia.bind(
      this,
    )
    this.els.items.element.addEventListener(
      'touchstart',
      this.handleTouchStartScrollToMedia,
      { passive: true },
    )

    if (this.els?.dots?.element && this.els?.items?.element) {
      this.listeners.add(
        this.els.dots.element,
        'wheel',
        this.onDotWheel.bind(this),
        { passive: false },
      )
      this.listeners.add(
        this.els.dots.element,
        'touchstart',
        this.onDotTouchStart.bind(this),
        { passive: true },
      )
      this.listeners.add(
        this.els.dots.element,
        'touchmove',
        this.onDotTouchMove.bind(this),
        { passive: false },
      )
    }

    if (
      this.els?.thumbnail?.exists &&
      this.els?.item?.exists &&
      this.els?.items?.exists
    ) {
      this.listeners.add(
        this.els.items.element,
        'scroll',
        this.onScroll.bind(this),
      )
    }

    this.listeners.add(
      this.els.items.element,
      'resize',
      debounce(this.checkViewportSize.bind(this), 100),
    )
  }

  onDrawerOpen (e) {
    if (e.detail.key === 'size-guide') {
      this.handleSizeGuideOpen()
    }
  }

  onScroll () {
    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.onMediaScroll()
  }

  checkViewportSize () {
    if (viewportSize() === `xs`) {
      document.body.classList.toggle(
        this.config.classes.desktopZoomClass,
        false,
      )
    }
  }

  handleSizeGuideOpen () {
    if (this.inViewImage && this.els?.items?.element) {
      this.els.items.element.scrollTo({
        top: this.inViewImage.offsetTop,
        behavior: 'instant',
      })
    }
  }

  onDotWheel (e) {
    e.preventDefault()
    this.els.items.element.scrollTop += e.deltaY
  }

  onDotTouchStart (e) {
    if (e.touches.length === 1) {
      this.touchStartY = e.touches[0].clientY
    }
  }

  onDotTouchMove (e) {
    if (e.touches.length === 1) {
      const currentY = e.touches[0].clientY
      const deltaY = this.touchStartY - currentY
      this.els.items.element.scrollTop += deltaY
      this.touchStartY = currentY
      e.preventDefault()
    }
  }

  handleTouchStartScrollToMedia () {
    const mediaTop = this.els.items.element.getBoundingClientRect().top

    if (mediaTop < 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  onMediaScroll () {
    this.getInViewItem()

    const mediaThumbnails = this.els.thumbnail.elements
    const currentIndex = this.inViewImage.dataset.index

    mediaThumbnails.forEach(thumbnail => {
      const isCurrent = thumbnail.dataset.index === currentIndex
      thumbnail.setAttribute('aria-current', isCurrent)

      const videoEl = thumbnail.querySelector(this.els.dotVideo?.selector)
      if (videoEl) {
        if (isCurrent) {
          videoEl.pause()
        } else {
          videoEl.play()
        }
      }
    })
  }

  getInViewItem () {
    let maxVisibleRatio = 0
    let currentItem

    this.els.item.elements.forEach(item => {
      const itemRect = item.getBoundingClientRect()
      const mediaContainerRect = this.els.items.element.getBoundingClientRect()

      const visibleTop = Math.max(itemRect.top, mediaContainerRect.top)
      const visibleBottom = Math.min(itemRect.bottom, mediaContainerRect.bottom)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)
      const ratio = visibleHeight / itemRect.height

      if (ratio > maxVisibleRatio) {
        maxVisibleRatio = ratio
        currentItem = item
      }
    })

    // Add 'inView' class to the most visible item, remove it from others
    this.els.item.elements.forEach(item => {
      if (item === currentItem) {
        item.classList.add(this.config.classes.itemInView)
      } else {
        item.classList.remove(this.config.classes.itemInView)
      }
    })

    this.inViewImage = currentItem || null
  }

  onThumbnailClick (e) {
    const targetIndex = e.currentTarget.dataset.index
    const mediaContainer = this.els.items.element
    const targetMedia = document.querySelector(
      `.prd-Media_Item[data-index="${targetIndex}"]`,
    )

    if (mediaContainer && targetMedia) {
      mediaContainer.scrollTo({
        top: targetMedia.offsetTop,
        behavior: 'smooth',
      })
    }
  }

  pinchZoom (imageElement) {
    this.imageElementScale = 1
    this.isScaled = false

    this.singleTouchX = 0
    this.singleTouchY = 0

    const start = {}

    // Calculate distance between two fingers
    const distance = event => {
      return Math.hypot(
        event.touches[0].pageX - event.touches[1].pageX,
        event.touches[0].pageY - event.touches[1].pageY,
      )
    }

    document.body.addEventListener(
      'touchstart',
      event => {
        if (event.target === imageElement) {
          if (event.touches.length === 2) {
            event.preventDefault() // Prevent page scroll

            // Calculate where the fingers have started on the X and Y axis
            start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2
            start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2
            start.distance = distance(event)

            document.body.classList.add(
              this.config.classes.pinchZoomActiveClass,
            )

            const initialTransformValue = document.querySelector(
              this.config.matches.mediaSlideActive,
            ).style.transform
            document.documentElement.style.setProperty(
              '--initial-transform',
              initialTransformValue,
            )
          }
        }
      },
      { passive: true },
    )

    imageElement.addEventListener(
      'touchmove',
      event => {
        if (event.touches.length === 2) {
          event.preventDefault() // Prevent page scroll

          this.isScaled = true

          // Safari provides event.scale as two fingers move on the screen
          // For other browsers just calculate the scale manually
          let scale
          if (event.scale) {
            scale = event.scale
          } else {
            const deltaDistance = distance(event)
            scale = deltaDistance / start.distance
          }
          this.imageElementScale = Math.min(Math.max(1, scale), 4)

          // Calculate how much the fingers have moved on the X and Y axis
          this.deltaX =
            ((event.touches[0].pageX + event.touches[1].pageX) / 2 - start.x) *
            2 // x2 for accelarated movement
          this.deltaY =
            ((event.touches[0].pageY + event.touches[1].pageY) / 2 - start.y) *
            2 // x2 for accelarated movement

          // Transform the image to make it grow and move with fingers
          const transform = `translateX(${this.deltaX}px) translateY(${this.deltaY}px) scale(${this.imageElementScale})`
          imageElement.style.transform = transform
          imageElement.style.WebkitTransform = transform
        }

        if (event.touches.length === 1 && this.isScaled === true) {
          event.preventDefault() // Prevent page scroll

          const movementX = event.touches[0].pageX - this.singleTouchX
          const movementY = event.touches[0].pageY - this.singleTouchY

          const newX = this.deltaX + movementX
          const newY = this.deltaY + movementY

          const transform = `translateX(${newX}px) translateY(${newY}px) scale(${this.imageElementScale})`
          imageElement.style.transform = transform
          imageElement.style.WebkitTransform = transform
        }
      },
      { passive: true },
    )

    imageElement.addEventListener(
      'touchend',
      event => {
        if (event.touches.length === 1) {
          this.singleTouchX = event.touches[0].pageX
          this.singleTouchY = event.touches[0].pageY
        }

        if (event.touches.length === 0) {
          // Reset image to it's original format
          imageElement.style.transform = ''
          imageElement.style.WebkitTransform = ''

          document.body.classList.remove(
            this.config.classes.pinchZoomActiveClass,
          )

          this.isScaled = false
        }
      },
      { passive: true },
    )
  }

  toggleDesktopZoom (e) {
    const button = e.target.closest(this.els.button.selector)
    if (!button) return

    const isZoomed = document.body.classList.contains(
      this.config.classes.desktopZoomClass,
    )

    document.body.classList.toggle(this.config.classes.desktopZoomClass)
    document.body.classList.toggle(this.config.classes.overflowClass)

    if (!isZoomed) {
      button.scrollIntoView()
    }
  }

  handleWheelEvent (e) {
    const mediaItems = this.els?.items?.element
    if (!mediaItems) return

    const delta = e.deltaY
    const isScrollingUp = delta < 0
    const isScrollingDown = delta > 0

    const pageScrollTop = window.scrollY
    const pageHeight = document.documentElement.scrollHeight
    const viewportHeight = window.innerHeight
    const atPageTop = pageScrollTop === 0
    const atPageBottom = pageScrollTop + viewportHeight >= pageHeight

    const mediaScrollTop = mediaItems.scrollTop
    const mediaMaxScroll = mediaItems.scrollHeight - mediaItems.clientHeight
    const atMediaBottom =
      Math.ceil(mediaScrollTop) >= Math.floor(mediaMaxScroll)

    // Handle page scroll first when scrolling up or if the media is at the bottom
    if (
      (isScrollingUp && !atPageTop) ||
      (isScrollingDown && atMediaBottom && !atPageBottom)
    ) {
      e.preventDefault()
      window.scrollBy(0, delta)
    }
  }

  get config () {
    return {
      matches: {
        media: '.pz-Media',
        mediaSlideActive: '.prd-Media_Item-active',
        mediaImages: '.prd-Media_Item .rsp-Image_Image',
      },
      classes: {
        pinchZoomActiveClass: 'lyt-Site-pinchZoomActive',
        desktopZoomClass: 'lyt-Site-desktopZoomActive',
        overflowClass: 'util-SiteOverflowed',
        itemInView: 'prd-Media_Item-inView',
      },
      elements: {
        mediaImages: document.querySelectorAll(
          '.pz-Media .rsp-Image_Image:not(.pz-Image)',
        ),
      },
    }
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia)
}
